import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Tooltip,
  Divider,
  Typography,
  alpha,
} from '@mui/material';
import {
  Home as HomeIcon,
  Assignment as TodoIcon,
  People as LeadsIcon,
  Business as ClientsIcon,
  School as TutorsIcon,
  Event as SessionsIcon,
  Assessment as ReportsIcon,
  Inventory as CatalogIcon,
  Settings as SettingsIcon,
  AssignmentInd as AssignmentsIcon,
  VideoCall as OnlineIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../auth/PermissionContext';
import { sidebarColors } from '../../theme';

interface SidebarProps {
  open: boolean;
  collapsed: boolean;
  onClose: () => void;
  width: number;
  collapsedWidth: number;
  isMobile: boolean;
}

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  badge?: number;
  permission?: {
    action: 'read' | 'manage';
    subject: 'Dashboard' | 'Todo' | 'Lead' | 'Client' | 'Tutor' | 'TutoringSession' | 'Report' | 'CatalogItem' | 'Assignment' | 'OnlineSession';
  };
}

const mainNavItems: NavItem[] = [
  {
    id: 'home',
    label: 'Home',
    path: '/',
    icon: <HomeIcon />,
    permission: { action: 'read', subject: 'Dashboard' },
  },
  {
    id: 'todos',
    label: 'Todos',
    path: '/todos',
    icon: <TodoIcon />,
    badge: 12, // This would come from API
    permission: { action: 'read', subject: 'Todo' },
  },
  {
    id: 'leads',
    label: 'Leads',
    path: '/leads',
    icon: <LeadsIcon />,
    permission: { action: 'read', subject: 'Lead' },
  },
  {
    id: 'clients',
    label: 'Clients',
    path: '/clients',
    icon: <ClientsIcon />,
    permission: { action: 'read', subject: 'Client' },
  },
  {
    id: 'tutors',
    label: 'Tutors',
    path: '/tutors',
    icon: <TutorsIcon />,
    permission: { action: 'read', subject: 'Tutor' },
  },
  {
    id: 'assignments',
    label: 'Assignments',
    path: '/assignments',
    icon: <AssignmentsIcon />,
    permission: { action: 'read', subject: 'Assignment' },
  },
  {
    id: 'sessions',
    label: 'Sessions',
    path: '/sessions',
    icon: <SessionsIcon />,
    permission: { action: 'read', subject: 'TutoringSession' },
  },
  {
    id: 'online-sessions',
    label: 'Online Sessions',
    path: '/online-sessions',
    icon: <OnlineIcon />,
    permission: { action: 'read', subject: 'OnlineSession' },
  },
];

const secondaryNavItems: NavItem[] = [
  {
    id: 'reports',
    label: 'Reports',
    path: '/reports',
    icon: <ReportsIcon />,
    permission: { action: 'read', subject: 'Report' },
  },
  {
    id: 'catalog',
    label: 'Catalog',
    path: '/catalog',
    icon: <CatalogIcon />,
    permission: { action: 'read', subject: 'CatalogItem' },
  },
];

const bottomNavItems: NavItem[] = [
  {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: <SettingsIcon />,
  },
];

export function Sidebar({
  open,
  collapsed,
  onClose,
  width,
  collapsedWidth,
  isMobile,
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const actualWidth = collapsed ? collapsedWidth : width;

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item: NavItem) => {
    // Check permissions
    if (item.permission && !can(item.permission.action, item.permission.subject)) {
      return null;
    }

    const active = isActive(item.path);

    const button = (
      <ListItemButton
        onClick={() => handleNavClick(item.path)}
        sx={{
          minHeight: 48,
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: 2.5,
          borderRadius: 1,
          mx: 1,
          mb: 0.5,
          color: active ? sidebarColors.textActive : sidebarColors.text,
          bgcolor: active ? alpha(sidebarColors.active, 0.16) : 'transparent',
          '&:hover': {
            bgcolor: active
              ? alpha(sidebarColors.active, 0.24)
              : sidebarColors.hover,
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: collapsed ? 0 : 2,
            justifyContent: 'center',
            color: active ? sidebarColors.active : sidebarColors.text,
          }}
        >
          {item.badge ? (
            <Badge
              badgeContent={item.badge}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.625rem',
                  height: 18,
                  minWidth: 18,
                },
              }}
            >
              {item.icon}
            </Badge>
          ) : (
            item.icon
          )}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 400,
            }}
          />
        )}
      </ListItemButton>
    );

    if (collapsed) {
      return (
        <Tooltip title={item.label} placement="right" key={item.id}>
          <ListItem disablePadding>{button}</ListItem>
        </Tooltip>
      );
    }

    return (
      <ListItem disablePadding key={item.id}>
        {button}
      </ListItem>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: sidebarColors.background,
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 1 : 3,
          py: 2,
          minHeight: 64,
        }}
      >
        <Box
          component="img"
          src="/vite.svg"
          alt="VT Logo"
          sx={{ width: 32, height: 32 }}
        />
        {!collapsed && (
          <Typography
            variant="h6"
            sx={{
              ml: 1.5,
              color: sidebarColors.textActive,
              fontWeight: 700,
              fontSize: '1rem',
            }}
          >
            VT Dashboard
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: alpha(sidebarColors.text, 0.12) }} />

      {/* Main Navigation */}
      <List sx={{ flexGrow: 1, py: 2 }}>
        {mainNavItems.map(renderNavItem)}

        <Divider sx={{ my: 2, borderColor: alpha(sidebarColors.text, 0.12) }} />

        {secondaryNavItems.map(renderNavItem)}
      </List>

      {/* Bottom Navigation */}
      <List sx={{ py: 1 }}>
        <Divider sx={{ mb: 1, borderColor: alpha(sidebarColors.text, 0.12) }} />
        {bottomNavItems.map(renderNavItem)}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: width,
            boxSizing: 'border-box',
            borderRight: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: actualWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: actualWidth,
          boxSizing: 'border-box',
          borderRight: 'none',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
